/*
 * /classes/:slug/products/:subId? — danh sách Product của lớp; subId = productId mở
 * màn hình chi tiết (đề bài, bài mẫu, hướng dẫn, nộp bài) ở chế độ tập trung.
 * Tự tải lại mỗi 2,5 giây khi có bài đang chờ chấm.
 */
import { useCallback, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Icon } from "@/components/icon"
import { EmptyState, ErrorState, Page, PageHeader, usePageTitle } from "@/components/page"
import { LearningModeExit } from "@/components/learning"
import { useClass, useFocusMode } from "@/app/ClassLayout"
import { api } from "@/lib/api"
import { ProductDetail } from "./products/detail"
import { QUEUED, StatusBadge } from "./products/shared"

export default function ProductsPage() {
  const { slug, base } = useClass()
  const { subId: productId = "" } = useParams()
  const [data, setData] = useState<any>()
  const [error, setError] = useState("")
  useFocusMode(!!productId)

  const load = useCallback(() => {
    setError("")
    return api(`/api/classes/${encodeURIComponent(slug)}/products`)
      .then(setData)
      .catch((e: any) => setError(e.message))
  }, [slug])
  useEffect(() => {
    setData(undefined)
    load()
  }, [load])

  const pending = !!data?.products?.some((p: any) => (p.submissions || []).some((s: any) => QUEUED.includes(s.status)))
  useEffect(() => {
    if (!pending) return
    const t = window.setInterval(load, 2500)
    return () => window.clearInterval(t)
  }, [pending, load])
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }) }, [productId])

  const products: any[] = data?.products || []
  const idx = productId ? products.findIndex((p) => p.id === productId) : -1
  const selected = idx >= 0 ? products[idx] : null
  usePageTitle(selected ? selected.name : "Products", "Các sản phẩm mà học viên cần tạo ra.")

  if (productId) {
    return (
      <Page className="max-w-[760px]">
        <div className="flex items-center justify-between gap-3">
          <LearningModeExit slug={slug} to={`${base}/products`} />
          {selected && products.length > 1 && <ProductStepper base={base} products={products} index={idx} />}
        </div>
        {error && <ErrorState message={error} onRetry={load} />}
        {!data && !error && <Skeleton className="h-64 rounded-[20px]" />}
        {data && !selected && (
          <EmptyState mascot="meerkat-thinking" title="Không tìm thấy Product này" description="Có thể Product đã được đổi hoặc xoá. Con quay lại danh sách nhé." />
        )}
        {selected && (
          <ProductDetail key={selected.id} product={selected} guidance={data.guidance || {}} slug={slug} reload={load} index={idx + 1} total={products.length} />
        )}
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader eyebrow="Learning" title="Products" description="Các sản phẩm mà con cần tạo ra trong khóa học." />
      {error && <ErrorState message={error} onRetry={load} />}
      {!data && !error && (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 rounded-[20px]" />
          ))}
        </div>
      )}
      {data && !products.length && <EmptyState mascot="giraffe-thinking" title="Chưa có Product" description="Mentor sẽ sớm thêm sản phẩm đầu tiên cho lớp." />}
      {products.length > 0 && <ProductList products={products} base={base} />}
    </Page>
  )
}

function ProductStepper({ base, products, index }: { base: string; products: any[]; index: number }) {
  const prev = products[index - 1]
  const next = products[index + 1]
  const cls = "grid size-9 place-items-center rounded-full border-[1.5px] border-edge bg-card text-ink transition-colors hover:border-orange"
  return (
    <div className="flex items-center gap-2">
      {prev ? (
        <Link to={`${base}/products/${encodeURIComponent(prev.id)}`} className={cls} aria-label="Product trước">
          <Icon name="chevron-left" className="size-4" />
        </Link>
      ) : (
        <span className={`${cls} opacity-40`} aria-hidden="true">
          <Icon name="chevron-left" className="size-4" />
        </span>
      )}
      {next ? (
        <Link to={`${base}/products/${encodeURIComponent(next.id)}`} className={cls} aria-label="Product tiếp theo">
          <Icon name="chevron-right" className="size-4" />
        </Link>
      ) : (
        <span className={`${cls} opacity-40`} aria-hidden="true">
          <Icon name="chevron-right" className="size-4" />
        </span>
      )}
    </div>
  )
}

/** Mỗi Product một thẻ riêng; trạng thái lấy từ phiên bản mới nhất. */
export function ProductList({ products, base }: { products: any[]; base: string }) {
  const reduce = useReducedMotion()
  return (
    <motion.ul
      className="flex flex-col gap-3"
      initial={reduce ? false : "hidden"}
      animate="shown"
      variants={{ hidden: {}, shown: { transition: { staggerChildren: 0.04 } } }}
    >
      {products.map((product, i) => {
        const latest = product.submissions?.[0]
        return (
          <motion.li key={product.id} variants={{ hidden: reduce ? {} : { y: 8 }, shown: { y: 0 } }}>
            <Link to={`${base}/products/${encodeURIComponent(product.id)}`} className="group block">
              <Card size="sm" className="transition-[transform,box-shadow] group-hover:-translate-y-0.5 group-hover:shadow-card-hover">
                <CardContent className="flex items-center gap-4">
                  {product.cover_image_url ? (
                    <img src={product.cover_image_url} alt="" loading="lazy" className="size-14 shrink-0 rounded-2xl object-cover" />
                  ) : (
                    <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-soft font-heading text-lg font-bold text-accent-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  )}
                  <span className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
                    <span className="line-clamp-2 font-heading text-base font-semibold text-ink sm:text-lg">{latest?.product_title || product.name}</span>
                    <StatusBadge status={latest?.status} />
                  </span>
                  <Icon name="arrow-right" className="size-5 shrink-0 transition-transform group-hover:translate-x-1" />
                </CardContent>
              </Card>
            </Link>
          </motion.li>
        )
      })}
    </motion.ul>
  )
}
